import type {NotificationConfigurationADto} from '@cohort/admin-schemas/notification';
import type {NotificationConfigurationType} from '@cohort/merchants/apps';
import {getNotificationIntegrations, NotificationConfigurationSchema} from '@cohort/merchants/apps';
import {useNotificationIntegrations} from '@cohort/merchants/apps/useNotificationIntegrations';
import Button from '@cohort/merchants/components/buttons/Button';
import SelectInput from '@cohort/merchants/components/form/v2/SelectInput';
import type {SelectOption} from '@cohort/merchants/components/form/v2/SelectPicker';
import Header from '@cohort/merchants/components/Header';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@cohort/merchants/components/modals/Dialog';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {handleFormErrors} from '@cohort/merchants/lib/form/utils';
import type {EventId, NotificationIntegrationId} from '@cohort/shared/schema/apps';
import {NotificationIntegrationIdSchema} from '@cohort/shared/schema/apps';
import {COHORT_EMAIL_SUPPORTED_USER_EVENTS_SPECS_IDS} from '@cohort/shared/schema/apps/cohort/notifications/email';
import type {NotificationConfigurationResourceType} from '@cohort/shared/schema/common/notificationConfiguration';
import {zodResolver} from '@hookform/resolvers/zod';
import React from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {z} from 'zod';

type CreateEditNotificationModalProps = {
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (data: NotificationConfigurationType) => void;
  userEventId: EventId;
  notificationConfiguration?: NotificationConfigurationADto;
  existingNotificationIntegrationIds?: Array<NotificationIntegrationId>;
  resourceType: NotificationConfigurationResourceType;
};

const CreateEditNotificationConfigurationModal: React.FC<CreateEditNotificationModalProps> = ({
  isLoading = false,
  onClose,
  onSubmit,
  notificationConfiguration,
  userEventId,
  existingNotificationIntegrationIds = [],
  resourceType,
}) => {
  const merchant = useCurrentMerchant();
  const {
    getNotificationIntegrationTitle,
    getNotificationIntegration,
    getAppFromNotificationIntegration,
  } = useNotificationIntegrations();
  const {t} = useTranslation('pages', {
    keyPrefix: 'settings.notifications.createEditNotificationModal',
  });

  const {setError, clearErrors, register, handleSubmit, watch, control} =
    useForm<NotificationConfigurationType>({
      resolver: zodResolver(NotificationConfigurationSchema),
      defaultValues: {
        merchantId: merchant.id,
        userEventId: userEventId,
        connectionId: notificationConfiguration?.connectionId ?? null,
        notificationIntegrationId: notificationConfiguration?.notificationIntegrationId ?? null,
        notificationIntegrationConfig:
          notificationConfiguration?.notificationIntegrationConfig ?? {},
        resourceType: notificationConfiguration?.resourceType ?? resourceType,
      },
    });

  const notificationIntegrations = getNotificationIntegrations();

  const [notificationIntegrationId] = watch(['notificationIntegrationId']);
  const selectedNotificationIntegration = notificationIntegrationId
    ? getNotificationIntegration(notificationIntegrationId)
    : null;

  const formatOptionLabel = (data: SelectOption): JSX.Element => {
    const {label, value} = data;
    const notificationIntegrationId = NotificationIntegrationIdSchema.parse(value);
    const app = getAppFromNotificationIntegration(notificationIntegrationId);

    return (
      <div className="flex items-center space-x-2">
        {React.cloneElement(app.logo, {height: 20, width: 20})}
        <div>{label}</div>
      </div>
    );
  };

  const nonExistingNotificationIntegrations = notificationIntegrations.filter(
    notificationIntegration =>
      !existingNotificationIntegrationIds.includes(
        notificationIntegration.spec.id as NotificationIntegrationId
      )
  );

  // @Devs - Remove unsupported user events from the select.
  const supportedNotificationIntegrations = nonExistingNotificationIntegrations.filter(
    notificationIntegration =>
      notificationIntegration.spec.id !== 'cohort.email' ||
      COHORT_EMAIL_SUPPORTED_USER_EVENTS_SPECS_IDS.includes(userEventId)
  );

  const selectInputOptions: Array<SelectOption> = supportedNotificationIntegrations.map(
    notificationIntegration => {
      return {
        value: notificationIntegration.spec.id,
        label: getNotificationIntegrationTitle(notificationIntegration),
      } satisfies SelectOption;
    }
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[800px]">
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit(data => {
              if (selectedNotificationIntegration === null) {
                return;
              }
              try {
                const validationSchema =
                  selectedNotificationIntegration.configComponentValidationSchema ??
                  selectedNotificationIntegration.spec.configSchema;
                const parsedConfig = validationSchema.parse(data.notificationIntegrationConfig);
                const parsedData = {
                  ...data,
                  notificationIntegrationConfig: parsedConfig,
                };
                onSubmit(parsedData);
              } catch (error) {
                if (error instanceof z.ZodError) {
                  handleFormErrors(error, clearErrors, setError, 'notificationIntegrationConfig.');
                } else {
                  throw error;
                }
              }
            })(e);
          }}
        >
          <DialogHeader className="space-y-6">
            <Header title={notificationConfiguration ? t('titleEdit') : t('titleCreate')} />
          </DialogHeader>
          <DialogBody className="pb-3 pt-0">
            <div className="w-[320px]">
              <SelectInput
                labelPosition="top"
                label={t('integrationDropdownLabel')}
                displayMode="expand"
                options={selectInputOptions}
                name="notificationIntegrationId"
                register={register}
                control={control}
                formatOptionLabel={formatOptionLabel}
              />
            </div>

            <div>
              {selectedNotificationIntegration && (
                <selectedNotificationIntegration.configComponent
                  register={register}
                  control={control}
                />
              )}
            </div>
          </DialogBody>
          <DialogFooter className="bg-white">
            <Button disabled={isLoading} variant="secondary" onClick={onClose}>
              {t('btnCancel')}
            </Button>
            <Button
              loading={isLoading}
              disabled={isLoading || selectedNotificationIntegration === null}
              type="submit"
            >
              {notificationConfiguration ? t('submitBtnEdit') : t('submitBtnCreate')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEditNotificationConfigurationModal;
